import styled from 'styled-components';

/** The voice note's play button, waveform scrubber and elapsed time. */

export const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-width: 0;
`;

export const PlayButton = styled.button`
  flex: none;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  background: ${({ theme }) => theme.primary};
  display: flex;
  align-items: center;
  justify-content: center;

  :disabled {
    cursor: default;
    opacity: 0.5;
  }

  svg {
    display: block;
    width: 12px;
    height: 12px;
  }
`;

/**
 * The bars are spread rather than gapped, so the 1px between them absorbs the
 * rounding at any width.
 *
 * The 140px basis is what gives the pill its size: the bubble around it is
 * shrink-to-fit, so nothing upstream has a width for flex-grow to divide or for
 * a percentage to resolve against - without it the wave collapses to exactly
 * the bars it happens to hold, and they end up touching.
 *
 * It has to be the BASIS and not a min-width, though. A floor cannot be shrunk
 * past, so in a narrow container - the corner chat bubble, a phone - the pill
 * grew wider than the 70% its column allows and overflowed to the side, landing
 * on top of the hover actions: the play button was simply not clickable. A basis
 * sizes the pill exactly the same when there is room and gives way when there
 * is not.
 *
 * The floor that remains is only what still reads as a waveform: the bars are
 * counted from this element's measured width, so they thin out with it instead
 * of crowding into each other.
 */
export const Wave = styled.div`
  position: relative;
  flex: 1 1 140px;
  min-width: 56px;
  height: 24px;
  cursor: pointer;
  /* Let the drag-to-seek win over the browser's scroll gesture on touch. */
  touch-action: none;
`;

/**
 * The bars, drawn twice: grey underneath, red on top, and the red copy clipped
 * to how far the recording has played.
 *
 * A single row with a played/unplayed flag per bar was the obvious version, and
 * it stepped - the fill could only ever land on a bar boundary, so a two second
 * clip advanced in 48ths. Clipping paints a bar half-full, and `clip-path`
 * changes nothing about layout, so both rows sit on exactly the same pixels.
 */
export const BarsRow = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const WaveBar = styled.span<{ $peak: number; $played?: boolean }>`
  flex: none;
  width: 2px;
  /* A floor, so silence is still a line rather than nothing. */
  height: ${({ $peak }) => Math.max(12, Math.round($peak * 100))}%;
  border-radius: 2px;
  background: ${({ theme, $played }) =>
    $played ? theme.primary : theme.mediumGray};
`;

export const Time = styled.span`
  flex: none;
  font-size: 11px;
  line-height: 14px;
  color: ${({ theme }) => theme.darkGray};
  font-variant-numeric: tabular-nums;
`;
